import QueryProvider from '@/components/providers/query-provider.tsx'
import { AppThemeProvider } from '@/components/providers/theme-provider'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider, useAuth } from '@/features/authentication/components/auth-provider.tsx'
import installHttpInterceptors from '@/lib/axios-interceptors'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode, Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import reportWebVitals from './reportWebVitals.ts'
// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { MembershipNotFound } from './routes/not-found'
import './styles.css'

// Create a new router instance
export const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
  defaultNotFoundComponent: MembershipNotFound,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

// Sentry.init({
//   dsn: import.meta.env.VITE_SENTRY_DSN,
//   integrations: [
//     // If you're using react router, use the integration for your react router version instead.
//     // Learn more at
//     // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
//     Sentry.browserTracingIntegration(),
//     Sentry.replayIntegration({
//       maskAllInputs: true,
//       maskAllText: false,
//     }),
//     Sentry.tanstackRouterBrowserTracingIntegration(router),
//     Sentry.browserProfilingIntegration(),
//   ],

//   // Set tracesSampleRate to 1.0 to capture 100%
//   // of transactions for tracing.
//   // Learn more at
//   // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
//   tracesSampleRate: 1.0,

//   // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
//   tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/, 'localhost'],

//   // Capture Replay for 10% of all sessions,
//   // plus for 100% of sessions with an error
//   // Learn more at
//   // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
//   replaysSessionSampleRate: 1.0,
//   replaysOnErrorSampleRate: 1.0,
//   // Set profilesSampleRate to 1.0 to profile every transaction.
//   // Since profilesSampleRate is relative to tracesSampleRate,
//   // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
//   // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
//   // results in 25% of transactions being profiled (0.5*0.5=0.25)
//   profilesSampleRate: 1.0,
// })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const ReactQueryDevtools =
  import.meta.env.DEV
    ? lazy(async () => ({
        default: (await import('@tanstack/react-query-devtools')).ReactQueryDevtools,
      }))
    : null

function App() {
  const auth = useAuth()
  return <RouterProvider router={router} context={{ auth }} />
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  // Install HTTP interceptors and idle/session manager once at startup
  installHttpInterceptors()
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <AppThemeProvider>
        <QueryProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
          {ReactQueryDevtools ? (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          ) : null}
        </QueryProvider>
        <Toaster richColors position='top-right' />
      </AppThemeProvider>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example, reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
